import { useTranslation } from 'react-i18next';

import {
  META_ERROR_DOCS,
  isMetaDocumentedCode,
} from 'src/utils/ErrorDirectory';

import { tWapiInlineErrorCardProps } from './WapiInlineErrorCard.types';

/**
 * The inline card shown in the chat when a WhatsApp API error has to be
 * explained on the message bubble itself.
 *
 * The surface is opaque on purpose. This card renders inside message bubbles,
 * and those backgrounds are not theme-stable — incoming and Lead Agent bubbles
 * carry no `dark:` variant — so a translucent tint has no fixed surface to
 * validate a foreground against. Painting its own ground is what makes the
 * contrast provable. The tokens are pinned by
 * src/styles/state-tokens.contrast.test.ts; never pair them with an opacity
 * modifier, which that matrix cannot model.
 *
 * Copy arrives already translated because the two callers resolve it
 * differently (the chat passes an `errors/errors` namespace, the dev catalog
 * does not). The code label is the one string this card owns, since its key is
 * fixed.
 */
export default function WapiInlineErrorCard(props: tWapiInlineErrorCardProps) {
  const { t } = useTranslation();
  const codeLabel = t('wapi.errorCode', {
    code: props.code,
    ns: 'errors/errors',
  });

  return (
    <div className="bg-warning-tint-surface border-l-4 border-warning px-3 py-2 rounded-r">
      <p className="font-bold text-sm text-warning-on-tint">{props.title}</p>
      <p className="text-xs text-warning-on-tint-muted mt-0.5">{props.message}</p>
      {props.code !== undefined && (
        <p
          className="text-xs text-warning-on-tint-muted mt-1.5"
          data-testid="wapi-error-code"
        >
          {isMetaDocumentedCode(props.code) ? (
            <a
              href={META_ERROR_DOCS}
              target="_blank"
              rel="noopener noreferrer"
              // `underline` carries the affordance without colour, so the link is
              // still identifiable as one. No hover/focus opacity modifier: the
              // contrast matrix cannot model a translucent foreground, so an
              // `opacity-80` state would be exactly the unproven pairing this
              // card was built to remove.
              className="underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warning"
            >
              {codeLabel}
            </a>
          ) : (
            codeLabel
          )}
        </p>
      )}
    </div>
  );
}
